/**
 * Lab Betfair Net Exposure list — `getBetfairBook` display helpers
 * (`directive.js` getBfBookNetExposure / getBetfairAmount).
 *
 * Lab table (CM / showBetfairBook): event title + runner-name headers,
 * then "Bf-Book - betfair" (ifWin) and "Bf-Book - local" (local horse amounts).
 */

import type { BetfairBookResponse, BetfairMarketPnL, LocalBetfairBook } from '@/api/map/workstation'
import type { HorseAmount } from '@/features/net-exposure/fixture'

/** Lab `getBfBookNetExposure(booksarray.betfairBook)` — local book NE. */
export function bfListNetExposure(local: LocalBetfairBook | null | undefined): number {
  if (local?.netExposure === undefined || local?.netExposure === null) return 0
  return Number(local.netExposure) || 0
}

/** Lab `getBetfairAmount(horseId, bookFromBetfair)` — selection `ifWin`. */
export function bfListAmountForHorse(
  horseId: string | undefined,
  bookFromBetfair: BetfairMarketPnL[] | undefined,
): number {
  if (horseId == null || horseId === '') return 0
  const pls = bookFromBetfair?.[0]?.profitAndLosses
  if (!pls?.length) return 0
  const hit = pls.find((pl) => String(pl.selectionId) === String(horseId))
  return Number(hit?.ifWin) || 0
}

function selectionId(horse: HorseAmount | undefined): string | undefined {
  if (!horse) return undefined
  const id = horse.selectionId?.trim()
  if (id) return id
  return horse.name || undefined
}

/** Lab `getRunnerHeaderName` — horse[0]=1, horse[2]=X, horse[1]=2. */
export function bfListRunnerHeaders(horses: HorseAmount[]): {
  one: string
  x: string
  two: string
} {
  return {
    one: horses[0]?.name?.trim() || '1',
    x: horses.length >= 3 ? horses[2]?.name?.trim() || 'X' : 'X',
    two: horses[1]?.name?.trim() || '2',
  }
}

export type BfListRunnerAmounts = {
  one: number
  x: number | null
  two: number
}

/** Lab Bf-Book - betfair columns — `ifWin` per Match Odds selection. */
export function bfListBetfairRunnerAmounts(
  horses: HorseAmount[],
  book: BetfairBookResponse | undefined,
): BfListRunnerAmounts {
  const bf = book?.bookFromBetfair
  return {
    one: bfListAmountForHorse(selectionId(horses[0]), bf),
    x: horses.length >= 3 ? bfListAmountForHorse(selectionId(horses[2]), bf) : null,
    two: bfListAmountForHorse(selectionId(horses[1]), bf),
  }
}

function localAmountForHorse(
  horseId: string | undefined,
  local: LocalBetfairBook | null | undefined,
): number {
  if (horseId == null || horseId === '' || !local?.horse?.length) return 0
  const hit = local.horse.find((h) => String(h.horse) === String(horseId))
  return Number(hit?.amount) || 0
}

/** Lab Bf-Book - local columns — `bookFromLocal.horse[].amount`. */
export function bfListLocalRunnerAmounts(
  horses: HorseAmount[],
  book: BetfairBookResponse | undefined,
): BfListRunnerAmounts {
  const local = book?.bookFromLocal
  return {
    one: localAmountForHorse(selectionId(horses[0]), local),
    x: horses.length >= 3 ? localAmountForHorse(selectionId(horses[2]), local) : null,
    two: localAmountForHorse(selectionId(horses[1]), local),
  }
}

/** True when getBetfairBook returned a usable local or exchange book. */
export function hasBetfairListBookData(book: BetfairBookResponse | undefined): boolean {
  if (!book) return false
  if (book.bookFromLocal != null) return true
  return Boolean(book.bookFromBetfair?.[0]?.profitAndLosses?.length)
}

/**
 * Betfair list — keep events whose Match Odds Bf-Book is ready (or failed).
 * Drops still-pending / empty shells so the page never flashes empty cards.
 */
export function filterEventsWithBetfairBook<T extends { matchOdds?: { marketId?: string } | null }>(
  events: readonly T[],
  byMarketId: ReadonlyMap<string, BetfairBookResponse>,
  failedIds: readonly string[] = [],
): T[] {
  const failed = new Set(failedIds)
  return events.filter((event) => {
    const marketId = event.matchOdds?.marketId
    if (!marketId) return false
    if (failed.has(marketId)) return true
    return hasBetfairListBookData(byMarketId.get(marketId))
  })
}
